import { Card, TagsDocument } from "@/types/v2";
import { Icon } from "@/components/Common/Icon";
import { TagsList } from "@/components/Common/TagsList";
import { firestoreToDateTime } from "@/lib/utils";
import { TagsRepository } from "@/repositories/tags";
import { card as cardRepository } from "@/repositories/cards";
import { CardTagsDialog } from "@/components/Dashboard/components/Card/CardTagsDialog";
import { buildTagEditPatch } from "@/lib/cards";

export interface RoDetailsSummaryProps {
  cardData: Card;
  tags?: TagsDocument;
  shopId: string;
}

const formatAppointment = (appointment: Card["appointment"]): string => {
  const start = firestoreToDateTime(appointment?.startAt)?.toLocaleString();
  const end = firestoreToDateTime(appointment?.endAt)?.toLocaleString();
  if (!start) return "No appointment set";
  return end ? `${start} - ${end}` : start;
};

export const RoDetailsSummary = ({
  cardData,
  tags,
  shopId,
}: RoDetailsSummaryProps) => {
  const completedJobCount = (cardData.jobs ?? []).filter(
    (job) => job.completedDate
  ).length;
  const totalJobCount = cardData.jobs?.length ?? 0;

  const handleEditTag = async (values: { tags: string[] }) => {
    await TagsRepository.addTag(shopId, values.tags, tags);
    await cardRepository.update(
      shopId,
      cardData.id,
      buildTagEditPatch(cardData, values)
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-foreground shadow-sm">
      <div className="flex flex-row items-center gap-2">
        <Icon name={"appointment"} size={16} />
        <span className="text-muted-foreground">Appointment</span>
        <span className="font-medium">
          {formatAppointment(cardData.appointment)}
        </span>
      </div>
      <div className="flex flex-row items-center gap-2">
        <Icon name={"dueOut"} size={16} />
        <span className="text-muted-foreground">Due Date</span>
        <span className="font-medium">
          {firestoreToDateTime(cardData.promisedTime)?.toLocaleString() ??
            "No due date set"}
        </span>
      </div>
      {cardData.label && (
        <div className="flex flex-row items-center gap-2">
          <Icon name={"tag"} size={16} />
          <span className="text-muted-foreground">Label</span>
          <span
            className="flex justify-center rounded-lg px-2 font-bold text-white"
            style={{ backgroundColor: cardData.backgroundColor }}
          >
            {cardData.label}
          </span>
        </div>
      )}
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <Icon name={"tags"} size={16} />
          <span className="text-muted-foreground">Tags</span>
          {cardData.tags && cardData.tags.length > 0 ? (
            <TagsList tags={cardData.tags} allTags={tags?.tags} />
          ) : (
            <span className="font-medium text-muted-foreground">None</span>
          )}
        </div>
        <CardTagsDialog
          cardData={cardData}
          onSubmit={handleEditTag}
          buttonIcon={<Icon name="plus" size={12} />}
          buttonTitle="Add Tag"
          buttonClasses="h-auto gap-1 p-0 text-xs"
        />
      </div>
      <div className="flex flex-row items-center gap-2">
        <Icon name={"waiter"} size={16} />
        <span className="text-muted-foreground">Waiter</span>
        <span className="font-medium">{cardData.isWaiter ? "Yes" : "No"}</span>
      </div>
      <div className="flex flex-row items-center gap-2">
        <Icon name={"clock"} size={16} />
        <span className="text-muted-foreground">Progress</span>
        <span className="font-medium">
          {completedJobCount}/{totalJobCount}
        </span>
      </div>
    </div>
  );
};
